import React, { useState, useEffect } from 'react';

interface HALDetailsModalProps {
    isVisible: boolean;
    onClose: () => void;
    resource: any;
}

const HALDetailsModal: React.FC<HALDetailsModalProps> = ({ isVisible, onClose, resource }) => {
    // État pour gérer la largeur du modal avec drag
    const [modalWidth, setModalWidth] = useState<number>(500); // Largeur par défaut
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStartX, setDragStartX] = useState<number>(0);
    const [dragStartWidth, setDragStartWidth] = useState<number>(0);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [clickStartX, setClickStartX] = useState<number>(0);

    const MIN_WIDTH = 320;
    const MAX_WIDTH = 1000;

    // Gestion du drag
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setIsResizing(true);
        setDragStartX(e.clientX);
        setClickStartX(e.clientX);
        setDragStartWidth(modalWidth);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const delta = dragStartX - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth + delta));
            setModalWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setTimeout(() => setIsResizing(false), 50);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStartX, dragStartWidth]);

    if (!isVisible || !resource) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-20 z-50 transition-opacity duration-300"
            onClick={() => {
                if (!isResizing) onClose();
            }}
        >
            <div
                className="fixed inset-y-0 right-0 bg-white dark:bg-mcdm-third-500 shadow-lg z-40 overflow-y-auto"
                style={{
                    width: `${modalWidth}px`,
                    transition: isDragging ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Handle de redimensionnement */}
                <div
                    onMouseDown={handleMouseDown}
                    className={`absolute top-0 left-0 bottom-0 w-1 cursor-ew-resize hover:bg-mcdm-secondary-400 transition-colors duration-200 group ${isDragging ? 'bg-mcdm-secondary-500' : 'bg-transparent'
                        }`}
                    style={{ zIndex: 50 }}
                />

                {/* Indicateur visuel du handle */}
                <div
                    onMouseDown={handleMouseDown}
                    className={`fixed w-1 h-16 bg-mcdm-secondary-400 dark:bg-mcdm-secondary-300 rounded-full cursor-ew-resize transition-opacity duration-200 ${isDragging ? 'opacity-100' : 'opacity-40 hover:opacity-100'
                        }`}
                    style={{
                        top: '50vh',
                        right: `${modalWidth}px`,
                        transform: 'translate(50%, -50%)',
                        zIndex: 60,
                        transition: isDragging ? 'opacity 0.2s' : 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s',
                    }}
                >
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1">
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 pr-8">
                            {decodeHtmlEntities(resource.titre)}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    {resource.isLoading ? (
                        <div className="p-6 flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mcdm-secondary-400"></div>
                        </div>
                    ) : (
                        <div className="overflow-y-auto custom-scrollbar space-y-6">

                            {/* Metadata Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                {resource.halMetadata?.publication?.date && (
                                    <div>
                                        <span className="block text-xs font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-1">
                                            Date de publication
                                        </span>
                                        <span className="text-mcdm-third-500 dark:text-mcdm-primary-100 font-medium">
                                            {new Date(resource.halMetadata.publication.date).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}

                                {resource.halMetadata?.publication?.type && (
                                    <div>
                                        <span className="block text-xs font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-1">
                                            Type
                                        </span>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                            {resource.halMetadata.publication.type}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Authors */}
                            {resource.halMetadata?.authors && resource.halMetadata.authors.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-2">
                                        Auteurs
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {resource.halMetadata.authors.map((author: string, idx: number) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-mcdm-primary-200 dark:bg-mcdm-third-100 text-mcdm-third-400 dark:text-mcdm-primary-100"
                                            >
                                                {author}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Abstract */}
                            {resource.halMetadata?.abstract && (
                                <div>
                                    <h3 className="text-sm font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-2">
                                        Résumé
                                    </h3>
                                    <p className="text-mcdm-third-500 dark:text-mcdm-primary-200 leading-relaxed text-justify">
                                        {resource.halMetadata.abstract}
                                    </p>
                                </div>
                            )}

                            {/* Keywords */}
                            {resource.halMetadata?.keywords && resource.halMetadata.keywords.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-2">
                                        Mots-clés
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {resource.halMetadata.keywords.map((keyword: string, idx: number) => (
                                            <span key={idx} className="text-sm text-mcdm-secondary-600 dark:text-mcdm-secondary-400 font-medium">
                                                #{keyword}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Journal / Conference info if available */}
                            {resource.halMetadata?.journal?.title && (
                                <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-100 dark:border-white/10">
                                    <h3 className="text-xs font-semibold uppercase tracking-wider text-mcdm-third-400 dark:text-mcdm-primary-200 mb-2">
                                        Publié dans
                                    </h3>
                                    <p className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                                        {resource.halMetadata.journal.title}
                                        {resource.halMetadata.journal.publisher && ` - ${resource.halMetadata.journal.publisher}`}
                                    </p>
                                    <div className="mt-2 text-sm text-mcdm-third-400 dark:text-mcdm-primary-200 flex gap-4">
                                        {resource.halMetadata.journal.volume && <span>Vol. {resource.halMetadata.journal.volume}</span>}
                                        {resource.halMetadata.journal.issue && <span>N° {resource.halMetadata.journal.issue}</span>}
                                        {resource.halMetadata.journal.pages && <span>pp. {resource.halMetadata.journal.pages}</span>}
                                    </div>
                                </div>
                            )}

                        </div>
                    )}

                    {/* Footer Actions */}
                    <div className="p-6 border-t border-gray-100 dark:border-gray-600 bg-gray-50/50 dark:bg-mcdm-third-400/50 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                        >
                            Fermer
                        </button>
                        <a
                            href={resource.source?.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-mcdm-secondary-400 hover:bg-mcdm-secondary-500 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center"
                        >
                            Voir sur HAL
                            <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div >
    );
};

// Fonction utilitaire pour décoder les entités HTML (ex: &#x27E8; -> ⟨)
function decodeHtmlEntities(text: string): string {
    if (!text) return '';
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
}

export default HALDetailsModal;
